import { useState, useEffect, useCallback } from 'react';
import {
  getAPMAlertRules,
  createAPMAlertRule,
  updateAPMAlertRule,
  deleteAPMAlertRule,
  seedDefaultAPMRules,
} from '../services/aiOperationsApi';

interface ActionResult {
  success: boolean;
  error?: string;
}

const useAPMAlertRules = () => {
  const [rules, setRules] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchRules = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAPMAlertRules();
      setRules(response?.data || response || []);
    } catch (err: unknown) {
      console.error('Failed to fetch APM alert rules:', err);
      setError((err instanceof Error ? err.message : null) || 'Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const createRule = useCallback(async (rule: Record<string, any>): Promise<ActionResult> => {
    setSaving(true);
    try {
      await createAPMAlertRule(rule);
      await fetchRules();
      return { success: true };
    } catch (err: unknown) {
      console.error('Failed to create alert rule:', err);
      return { success: false, error: (err instanceof Error ? err.message : undefined) ?? 'Failed to create rule' };
    } finally {
      setSaving(false);
    }
  }, [fetchRules]);

  const updateRule = useCallback(async (ruleId: string, updates: Record<string, any>): Promise<ActionResult> => {
    setSaving(true);
    try {
      await updateAPMAlertRule(ruleId, updates);
      await fetchRules();
      return { success: true };
    } catch (err: unknown) {
      console.error('Failed to update alert rule:', err);
      return { success: false, error: (err instanceof Error ? err.message : undefined) ?? 'Failed to update rule' };
    } finally {
      setSaving(false);
    }
  }, [fetchRules]);

  const deleteRule = useCallback(async (ruleId: string): Promise<ActionResult> => {
    setSaving(true);
    try {
      await deleteAPMAlertRule(ruleId);
      await fetchRules();
      return { success: true };
    } catch (err: unknown) {
      console.error('Failed to delete alert rule:', err);
      return { success: false, error: (err instanceof Error ? err.message : undefined) ?? 'Failed to delete rule' };
    } finally {
      setSaving(false);
    }
  }, [fetchRules]);

  const toggleRule = useCallback(async (ruleId: string, enabled: boolean): Promise<ActionResult> => {
    return updateRule(ruleId, { enabled });
  }, [updateRule]);

  const seedDefaults = useCallback(async (): Promise<ActionResult> => {
    setSaving(true);
    try {
      await seedDefaultAPMRules();
      await fetchRules();
      return { success: true };
    } catch (err: unknown) {
      console.error('Failed to seed default rules:', err);
      return { success: false, error: (err instanceof Error ? err.message : undefined) ?? 'Failed to seed defaults' };
    } finally {
      setSaving(false);
    }
  }, [fetchRules]);

  return {
    rules,
    loading,
    error,
    saving,
    refetch: fetchRules,
    createRule,
    updateRule,
    deleteRule,
    toggleRule,
    seedDefaults,
  };
};

export default useAPMAlertRules;
